"use client";

import { useState, type ReactNode } from "react";

/**
 * The technical part of a status, a card or a reply, folded away. What a
 * person reads by default is a plain sentence; the ids, attempt counts,
 * step lists and verbatim error texts a developer needs sit under this,
 * collapsed until clicked. A native <details> where block content is
 * allowed; inside a paragraph (the approval control sits in a reply's
 * markdown) `inline` draws the same thing from spans and a button, since a
 * <details> inside a <p> is invalid HTML and the browser would break the
 * paragraph around it.
 * See docs/superpowers/specs/2026-10-14-plain-words-read-more-design.md.
 */
export default function ReadMore({ children, label = "Read more", className = "", inline = false }: { children: ReactNode; label?: string; className?: string; inline?: boolean }) {
  const [open, setOpen] = useState(false);
  const summary = (
    <>
      <span aria-hidden="true" className={`mr-1 inline-block transition-transform ${inline ? (open ? "rotate-90" : "") : "group-open/readmore:rotate-90"}`}>›</span>
      {label}
    </>
  );
  if (inline) {
    return (
      <span className={`block text-xs text-zinc-400 ${className}`}>
        <button type="button" aria-expanded={open} onClick={() => setOpen((value) => !value)} className="cursor-pointer text-[11px] text-zinc-500 hover:text-zinc-300">
          {summary}
        </button>
        {open && <span className="mt-1.5 block space-y-0.5 break-words">{children}</span>}
      </span>
    );
  }
  return (
    <details className={`group/readmore text-xs text-zinc-400 ${className}`}>
      <summary className="cursor-pointer select-none list-none text-[11px] text-zinc-500 hover:text-zinc-300 [&::-webkit-details-marker]:hidden">
        {summary}
      </summary>
      <div className="mt-1.5 space-y-0.5 break-words">{children}</div>
    </details>
  );
}
